import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "~/components/modal/ConfirmationModal";
import ReceptionSettingsErrorModal from "~/components/modal/ReceptionSettingsErrorModal";
import { apiClient, buildIfaceApiUrl, createIfaceHeaders } from "~/utils/apiClient";
import {
  getAppSettingAutoUpdateData,
  clearAppSettingsData,
  getPhoneBookAutoUpdateData,
  fetchAndSavePhoneBooksEntity,
  saveAppSettingsData,
  getActivationData,
  saveAppSettingAutoUpdateData,
  savePhoneBookAutoUpdateData,
  touchAppSettingAutoUpdateNow,
  touchPhoneBookAutoUpdateNow,
  initAppSettingAutoUpdateData,
  initPhoneBookAutoUpdateData,
  type PhoneBooksData,
  type AppSettingsResponse,
  type ActivationCheckResponse,
} from "~/utils/receptionDB";
import "~/css/PhonebookAppSettingsDownload.css";
import "~/css/Button.css";
import { resolveAutoUpdateTimesFromCheck, syncPhoneBooksData } from "~/utils/receptionUtils";
import ReceptionCommunicatingIndicator from "~/components/ReceptionCommunicatingIndicator";

const phoneBooksUrl = buildIfaceApiUrl("v1/phone-books");
const appSettingsUrl = buildIfaceApiUrl("v1/app-settings");
const activationCheckUrl = buildIfaceApiUrl("v1/activation/check");

type DownloadTarget = "phonebook" | "appSettings";

/** 電話帳・アプリ設定の手動ダウンロード UI（確認/成功/エラーの各モーダルを含む） */
const PhonebookAppSettingsDownload: React.FC = () => {
  const { t } = useTranslation();
  const [target, setTarget] = useState<DownloadTarget | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [successMessageKey, setSuccessMessageKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isActivationSaved, setIsActivationSaved] = useState(false);
  const [phoneBookStartDateTimeDisplay, setPhoneBookStartDateTimeDisplay] = useState("");
  const [appSettingUpdateDateTimeDisplay, setAppSettingUpdateDateTimeDisplay] = useState("");

  useEffect(() => {
    // eslint-disable-next-line complexity
    const initialize = async () => {
      const activation = await getActivationData();
      if (!activation) {
        setIsActivationSaved(false);
        return;
      }
      setIsActivationSaved(true);

      const [phoneBookAutoUpdate, appSettingAutoUpdate] = await Promise.all([
        getPhoneBookAutoUpdateData(),
        getAppSettingAutoUpdateData(),
      ]);

      // 初期表示
      setPhoneBookStartDateTimeDisplay(
        phoneBookAutoUpdate?.updateDate ?? t("phonebookDownloadNone")
      );
      setAppSettingUpdateDateTimeDisplay(
        appSettingAutoUpdate?.updateDate ?? t("phonebookDownloadNone")
      );

      // activation/check の予約時刻(reservation)と保存済みの updateDate を比較し、
      // 異なる場合は init で上書きする。updateDate を最新の予約時刻へ更新すると同時に
      // lastUpdateDate を null に戻し、待受側で変更後のコンテンツを再ダウンロードさせる。
      // 表示も最新の予約時刻へ更新する。
      try {
        const headers = createIfaceHeaders(true);
        const checkRes = await apiClient.get<{ data: ActivationCheckResponse }>(
          activationCheckUrl,
          {
            headers,
          }
        );
        const payload = checkRes?.data?.data as ActivationCheckResponse | undefined;

        const { appSettingUpdateDateTime: formattedApp, phoneBookStartDateTime: formattedPhone } =
          resolveAutoUpdateTimesFromCheck(payload, activation.timezone);

        if (formattedApp !== (appSettingAutoUpdate?.updateDate ?? null)) {
          await initAppSettingAutoUpdateData(formattedApp);
          setAppSettingUpdateDateTimeDisplay(formattedApp ?? t("phonebookDownloadNone"));
        }

        if (formattedPhone !== (phoneBookAutoUpdate?.updateDate ?? null)) {
          await initPhoneBookAutoUpdateData(formattedPhone);
          setPhoneBookStartDateTimeDisplay(formattedPhone ?? t("phonebookDownloadNone"));
        }
      } catch (err) {
        // 無害なチェックなのでエラー時はログを残して続行
        console.error("Failed to check activation/check for auto-update init:", err);
      }
    };
    initialize();
  }, []);

  // IndexedDB 全削除通知を受け取り、未アクティベーション状態にリセットする
  useEffect(() => {
    const handler = () => {
      setIsActivationSaved(false);
      setPhoneBookStartDateTimeDisplay("");
      setAppSettingUpdateDateTimeDisplay("");
    };
    window.addEventListener("reception:activationDataCleared", handler);
    return () => window.removeEventListener("reception:activationDataCleared", handler);
  }, []);

  // 他コンポーネントからのダウンロード完了通知を受け取り、
  // ボタンを有効化するためのリスナーを追加する
  useEffect(() => {
    const handler = async () => {
      try {
        setIsActivationSaved(true);

        // ダウンロード時に更新される表示日時も再取得する
        const [phoneBookAutoUpdate, appSettingAutoUpdate] = await Promise.all([
          getPhoneBookAutoUpdateData(),
          getAppSettingAutoUpdateData(),
        ]);

        setPhoneBookStartDateTimeDisplay(
          phoneBookAutoUpdate?.updateDate ?? t("phonebookDownloadNone")
        );
        setAppSettingUpdateDateTimeDisplay(
          appSettingAutoUpdate?.updateDate ?? t("phonebookDownloadNone")
        );
      } catch (err) {
        // リスナー内の非同期処理で例外が発生しても未処理の拒否にならないようにする
        // 必要に応じて監視ツールに送るなど拡張してください

        console.error("Error in reception:phonebooksAndAppSettingsDownloaded handler:", err);
      }
    };

    window.addEventListener(
      "reception:phonebooksAndAppSettingsDownloaded",
      handler as EventListener
    );
    return () => {
      window.removeEventListener(
        "reception:phonebooksAndAppSettingsDownloaded",
        handler as EventListener
      );
    };
  }, [t]);

  const confirmationMessage = useMemo(() => {
    if (target === "phonebook") return t("phonebookDownloadConfirmMessage");
    if (target === "appSettings") return t("appSettingsDownloadConfirmMessage");
    return "";
  }, [target, t]);

  const successMessage = useMemo(() => {
    if (!successMessageKey) {
      return "";
    }

    return t(successMessageKey);
  }, [successMessageKey, t]);

  const openConfirmModal = (nextTarget: DownloadTarget) => {
    setTarget(nextTarget);
    setShowConfirmModal(true);
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    setTarget(null);
  };

  const applyError = (error: unknown, currentTarget: DownloadTarget) => {
    const isErrorSaveFail = error instanceof Error && error.message === "PHONEBOOK_SAVE_FAILED";
    const code =
      (error as { response?: { data?: { errorCode?: number | string } } })?.response?.data
        ?.errorCode ?? "unknown";
    setErrorKey(
      isErrorSaveFail ? t("phonebookDownloadError") : t(`ifaceErrorCode.${currentTarget}.${code}`)
    );
    setShowErrorModal(true);
  };

  const saveDownloadResponse = async (currentTarget: DownloadTarget, responseData: unknown) => {
    if (currentTarget !== "phonebook") {
      await clearAppSettingsData();
      await saveAppSettingsData(responseData as AppSettingsResponse);
      return;
    }

    const phoneBooksData = (responseData as { data: PhoneBooksData }).data;
    const responseDataUrl = phoneBooksData.responseDataUrl;
    const phoneBooksEntity = await fetchAndSavePhoneBooksEntity(
      responseDataUrl,
      phoneBooksData,
      false
    );
    if (!phoneBooksEntity) {
      throw new Error("PHONEBOOK_SAVE_FAILED");
    }
    await syncPhoneBooksData(phoneBooksEntity);
  };

  // eslint-disable-next-line complexity
  const handleConfirmDownload = async () => {
    if (!target) {
      handleConfirmClose();
      return;
    }

    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      const url = target === "phonebook" ? phoneBooksUrl : appSettingsUrl;
      const headers = createIfaceHeaders(true);
      const response = await apiClient.get<unknown>(url, {
        headers,
      });

      await saveDownloadResponse(target, response.data);
      // 更新日時を activation/check から取得して保存する
      try {
        const activation = await getActivationData();
        const headers2 = createIfaceHeaders(true);
        const checkRes = await apiClient.get<{ data: ActivationCheckResponse }>(
          activationCheckUrl,
          {
            headers: headers2,
          }
        );
        const payload = checkRes?.data?.data as ActivationCheckResponse | undefined;

        const { appSettingUpdateDateTime: formattedApp, phoneBookStartDateTime: formattedPhone } =
          resolveAutoUpdateTimesFromCheck(payload, activation?.timezone);

        if (target === "phonebook") {
          if (formattedPhone) {
            await savePhoneBookAutoUpdateData(formattedPhone);
          } else {
            // フォールバック: 現在時刻を保存
            await savePhoneBookAutoUpdateData(new Date().toISOString());
          }
          try {
            await touchPhoneBookAutoUpdateNow();
          } catch (e) {
            console.error("Failed to touch phoneBook auto update time after manual download:", e);
          }
          setPhoneBookStartDateTimeDisplay(formattedPhone ?? t("phonebookDownloadNone"));
        }

        if (target === "appSettings") {
          if (formattedApp) {
            await saveAppSettingAutoUpdateData(formattedApp);
          } else {
            await saveAppSettingAutoUpdateData(new Date().toISOString());
          }
          try {
            await touchAppSettingAutoUpdateNow();
          } catch (e) {
            console.error("Failed to touch appSetting auto update time after manual download:", e);
          }
          setAppSettingUpdateDateTimeDisplay(formattedApp ?? t("phonebookDownloadNone"));
        }
      } catch (err) {
        // 非致命: 更新日時の保存に失敗してもダウンロード自体は成功扱い
        console.error("Failed to update auto-update timestamps after manual download:", err);
      }

      setSuccessMessageKey(
        target === "phonebook" ? "phonebookDownloadSuccess" : "appSettingsDownloadSuccess"
      );
      setShowSuccessModal(true);
    } catch (error) {
      applyError(error, target);
    } finally {
      setTarget(null);
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="phonebook-app-settings-download">
        <div className="phonebook-app-settings-download-content">
          <div className="phonebook-app-settings-download-row">
            <span className="phonebook-app-settings-download-label">
              {t("phonebookAutoUpdateDateTime")}
            </span>
            <span className="phonebook-app-settings-download-value phone-book-start-date-time">
              {phoneBookStartDateTimeDisplay}
            </span>
            <button
              type="button"
              className="button-1 phonebook-app-settings-download-button"
              onClick={() => openConfirmModal("phonebook")}
              disabled={isLoading || !isActivationSaved}
            >
              {isLoading && target === "phonebook"
                ? t("phonebookDownloading")
                : t("phonebookDownloadPhoneBookButton")}
            </button>
          </div>

          <div className="phonebook-app-settings-download-row">
            <span className="phonebook-app-settings-download-label">
              {t("appSettingsAutoUpdateDateTime")}
            </span>
            <span className="phonebook-app-settings-download-value app-settings-update-date-time">
              {appSettingUpdateDateTimeDisplay}
            </span>
            <button
              type="button"
              className="button-1 phonebook-app-settings-download-button"
              onClick={() => openConfirmModal("appSettings")}
              disabled={isLoading || !isActivationSaved}
            >
              {isLoading && target === "appSettings"
                ? t("appSettingsDownloading")
                : t("phonebookDownloadAppSettingsButton")}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showConfirmModal}
        handleClose={handleConfirmClose}
        handleConfirm={handleConfirmDownload}
        title={t("phonebookDownloadConfirmTitle")}
        body={confirmationMessage}
        cancelText={t("cancel")}
        confirmText={t("ok")}
      />

      <ConfirmationModal
        show={showSuccessModal}
        handleClose={() => {
          setShowSuccessModal(false);
          setSuccessMessageKey(null);
        }}
        handleConfirm={() => {
          setShowSuccessModal(false);
          setSuccessMessageKey(null);
        }}
        title={t("saved")}
        body={successMessage}
        cancelText=""
        confirmText={t("ok")}
      />

      <ReceptionSettingsErrorModal
        show={showErrorModal}
        onClose={() => {
          setShowErrorModal(false);
          setErrorKey(null);
        }}
        title={t("error")}
        body={errorKey ?? ""}
        confirmText={t("close")}
      />
      {isLoading && <ReceptionCommunicatingIndicator />}
    </>
  );
};

export default PhonebookAppSettingsDownload;
